import React, { useState, useEffect } from 'react';
import {
  Truck,
  Plus,
  Search,
  Eye,
  Package,
  MapPin
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { deliveryApi, salesOrdersApi } from '../../lib/api';
import { toast } from 'sonner';

const DeliveryOrders = () => {
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shipModal, setShipModal] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  
  const [formData, setFormData] = useState({
    sales_order_id: '',
    shipping_address: '',
    contact_person: '',
    contact_phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [doRes, soRes] = await Promise.all([
        deliveryApi.getAll(),
        salesOrdersApi.getAll('confirmed')
      ]);
      setDeliveryOrders(doRes.data || []);
      setSalesOrders(soRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load delivery orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await deliveryApi.create(formData);
      toast.success('Delivery order created successfully');
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Error creating DO:', err);
      toast.error(err.response?.data?.detail || 'Failed to create delivery order');
    }
  };

  const handleShip = async () => {
    if (!trackingNumber) {
      toast.error('Please enter tracking number');
      return;
    }
    try {
      await deliveryApi.ship(shipModal.id, trackingNumber);
      toast.success('Order marked as shipped');
      setShipModal(null);
      setTrackingNumber('');
      fetchData();
    } catch (err) {
      toast.error('Failed to ship order');
    }
  };

  const handleDeliver = async (id) => {
    try {
      await deliveryApi.deliver(id);
      toast.success('Order marked as delivered');
      fetchData();
    } catch (err) {
      toast.error('Failed to update delivery status');
    }
  };

  const resetForm = () => {
    setFormData({
      sales_order_id: '',
      shipping_address: '',
      contact_person: '',
      contact_phone: '',
      notes: ''
    });
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
    };
    return classes[status] || 'status-processing';
  };

  const filteredOrders = deliveryOrders.filter(order => {
    const matchesSearch = order.do_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="space-y-6" data-testid="delivery-orders-loading">
        <div className="skeleton h-12 w-48 rounded-md"></div>
        <div className="skeleton h-96 rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="delivery-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Delivery Orders</h1>
          <p className="text-slate-600 mt-1">{deliveryOrders.length} delivery orders</p>
        </div>
        <Button 
          className="bg-orange-600 hover:bg-orange-700 text-white"
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          data-testid="create-do-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create DO
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by DO number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {filteredOrders.length === 0 ? (
            <div className="empty-state py-16">
              <Truck className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No delivery orders found</h3>
              <p className="text-slate-500">Create a delivery order from confirmed sales orders.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>DO #</TableHead>
                    <TableHead>Sales Order</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tracking</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.id} data-testid={`do-row-${order.id}`}>
                      <TableCell className="font-mono text-sm">{order.do_number}</TableCell>
                      <TableCell className="font-mono text-sm">{order.sales_order_number}</TableCell>
                      <TableCell>{order.customer_name}</TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadgeClass(order.status)} capitalize`}>
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {order.tracking_number || '-'}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {new Date(order.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8"
                            onClick={() => setViewOrder(order)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {order.status === 'processing' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-8 text-blue-600"
                              onClick={() => setShipModal(order)}
                            >
                              Ship
                            </Button>
                          )}
                          {order.status === 'shipped' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-8 text-green-600"
                              onClick={() => handleDeliver(order.id)}
                            >
                              Deliver
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create DO Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Delivery Order</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Sales Order</Label>
                <Select 
                  value={formData.sales_order_id} 
                  onValueChange={(value) => setFormData({ ...formData, sales_order_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select confirmed sales order" />
                  </SelectTrigger>
                  <SelectContent>
                    {salesOrders.map(order => (
                      <SelectItem key={order.id} value={order.id}>
                        {order.order_number} - {order.customer_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">Shipping Address</Label>
                <Textarea
                  value={formData.shipping_address}
                  onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
                  placeholder="Full shipping address"
                  required
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Contact Person</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label className="form-label">Contact Phone</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="form-label">Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Delivery instructions..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700" data-testid="submit-do-button">
                Create DO
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ship Modal */}
      <Dialog open={!!shipModal} onOpenChange={() => setShipModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Ship Order</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="form-label">Tracking Number</Label>
            <Input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Enter tracking number"
              data-testid="tracking-number-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShipModal(null)}>
              Cancel
            </Button>
            <Button onClick={handleShip} className="bg-blue-600 hover:bg-blue-700">
              Mark as Shipped
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View DO Modal */}
      <Dialog open={!!viewOrder} onOpenChange={() => setViewOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>DO {viewOrder?.do_number}</DialogTitle>
          </DialogHeader>
          {viewOrder && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Sales Order</span>
                  <p className="font-mono">{viewOrder.sales_order_number}</p>
                </div>
                <div>
                  <span className="text-slate-500">Customer</span>
                  <p className="font-medium">{viewOrder.customer_name}</p>
                </div>
                <div>
                  <span className="text-slate-500">Status</span>
                  <Badge className={`${getStatusBadgeClass(viewOrder.status)} capitalize mt-1`}>
                    {viewOrder.status}
                  </Badge>
                </div>
                {viewOrder.tracking_number && (
                  <div>
                    <span className="text-slate-500">Tracking #</span>
                    <p className="font-mono">{viewOrder.tracking_number}</p>
                  </div>
                )}
              </div>

              <div className="bg-slate-50 rounded-md p-4">
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-medium">{viewOrder.contact_person}</p>
                    <p className="text-slate-500">{viewOrder.contact_phone}</p>
                    <p className="text-slate-600 mt-1">{viewOrder.shipping_address}</p>
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewOrder.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell className="text-right font-mono">{item.quantity} {item.unit}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {viewOrder.shipped_date && (
                <p className="text-sm text-slate-500">
                  Shipped: {new Date(viewOrder.shipped_date).toLocaleDateString()}
                </p>
              )}
              {viewOrder.delivered_date && (
                <p className="text-sm text-green-600">
                  Delivered: {new Date(viewOrder.delivered_date).toLocaleDateString()}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryOrders;
